import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Search, Edit, Camera, UserPlus, UserX, User, CheckCircle, AlertCircle, X, Image, 
  Phone, Home, Briefcase, XCircle, HardDrive, Fingerprint, Trash2, ShieldCheck
} from 'lucide-react';
import * as faceapi from 'face-api.js';
import { api } from '../utils/api';
import { getCameraConstraints, getSimpleCameraConstraints } from '../utils/helpers';
import Swal from 'sweetalert2';

const academicPositions = [
  'ครูผู้ช่วย',
  'ครู',
  'ครูชำนาญการ',
  'ครูชำนาญการพิเศษ',
  'ครูเชี่ยวชาญ',
  'ครูเชี่ยวชาญพิเศษ',
  'ครูอัตราจ้าง',
  'ลูกจ้าง(ชั่วคราว)',
  'นักศึกษาฝึกประสบการณ์วิชาชีพครู'
];

const EmployeeManagement = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // State สำหรับดูข้อมูลในเครื่องสแกน
  const [showDeviceUsersModal, setShowDeviceUsersModal] = useState(false);
  const [deviceUsers, setDeviceUsers] = useState([]);
  const [loadingDeviceUsers, setLoadingDeviceUsers] = useState(false);
  
  const [newEmployee, setNewEmployee] = useState({
    employee_id: '',
    national_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    position: '',
    academic_position: '',
    department: '',
    organization_id: user?.organization_id || 1, 
    save_attendance_image: false
  });

  const [cameraActive, setCameraActive] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const isStaff = user?.is_staff === true;
  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = employees;

    if (isStaff) {
      filtered = filtered.filter(emp => 
        emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ'
      );
    }

    if (statusFilter === 'active') {
      filtered = filtered.filter(emp => emp.is_active == 1);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(emp => emp.is_active == 0);
    }

    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.department && emp.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.position && emp.position.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.academic_position && emp.academic_position.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.phone && emp.phone.includes(searchTerm))
      );
    }

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, statusFilter, isStaff]);


  const loadData = async () => {
    setLoading(true);
    try {
      const [empData, deptData] = await Promise.all([
        api.getEmployees(),
        api.getDepartments()
      ]);

      const sortedEmpData = empData.sort((a, b) => {
        const activeCompare = b.is_active - a.is_active;
        if (activeCompare !== 0) {
          return activeCompare;
        }

        const deptA = a.department || '';
        const deptB = b.department || '';
        
        const deptCompare = deptA.localeCompare(deptB);
        if (deptCompare !== 0) {
          return deptCompare;
        }
        
        return a.employee_id.localeCompare(b.employee_id);
      });
      
      setEmployees(sortedEmpData);
      setDepartments(deptData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    try {
      const employeeData = {
        ...newEmployee,
        organization_id: user?.organization_id || 1,
        save_attendance_image: isAdmin ? newEmployee.save_attendance_image : true
      };

      const data = await api.addEmployee(employeeData);

      if (data.success) {
        setShowAddModal(false);
        setNewEmployee({
          employee_id: '',
          national_id: '',
          first_name: '',
          last_name: '',
          phone: '',
          address: '',
          position: '',
          academic_position: '',
          department: '',
          organization_id: user?.organization_id || 1,
          save_attendance_image: true
        });
        
        loadData();
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มบุคลากรเรียบร้อยแล้ว',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถเพิ่มบุคลากรได้: ' + data.error, 'error');
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleEditEmployee = async () => {
    try {
      const updateData = {
        employee_id: selectedEmployee.employee_id,
        national_id: selectedEmployee.national_id,
        first_name: selectedEmployee.first_name,
        last_name: selectedEmployee.last_name,
        phone: selectedEmployee.phone,
        address: selectedEmployee.address,
        position: selectedEmployee.position,
        academic_position: selectedEmployee.academic_position,
        department: selectedEmployee.department,
        is_active: selectedEmployee.is_active
      };
  
      if (!isStaff && selectedEmployee.save_attendance_image !== undefined) {
        updateData.save_attendance_image = selectedEmployee.save_attendance_image;
      }
  
      const data = await api.updateEmployee(updateData);
  
      if (data.success) {
        setShowEditModal(false);
        
        setEmployees(prevEmployees => 
          prevEmployees.map(emp => 
            emp.employee_id === selectedEmployee.employee_id 
              ? { ...emp, ...updateData }
              : emp
          )
        );
        
        setSelectedEmployee(null);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'แก้ไขข้อมูลบุคลากรเรียบร้อยแล้ว',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถแก้ไขข้อมูลได้: ' + data.error, 'error');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleDeactivateEmployee = async (employeeId, firstName, lastName) => {
    const result = await Swal.fire({
      title: 'ยืนยันการปิดใช้งาน',
      text: `ต้องการปิดการใช้งานของ ${firstName} ${lastName} หรือไม่?\nการดำเนินการนี้จะทำให้บุคลากรไม่สามารถใช้งานระบบได้`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ปิดใช้งาน',
      cancelButtonText: 'ยกเลิก'
    });

    if (!result.isConfirmed) return;

    try {
      const data = await api.deactivateEmployee(employeeId);

      if (data.success) {
        setEmployees(prev => prev.map(emp => emp.employee_id === employeeId ? { ...emp, is_active: 0 } : emp));
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'ปิดการใช้งานบุคลากรเรียบร้อยแล้ว',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถปิดการใช้งานได้: ' + data.error, 'error');
      }
    } catch (error) {
      console.error('Error deactivating employee:', error);
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleReactivateEmployee = async (employeeId, firstName, lastName) => {
    const result = await Swal.fire({
      title: 'ยืนยันการเปิดใช้งาน',
      text: `ต้องการเปิดการใช้งานของ ${firstName} ${lastName} หรือไม่?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'เปิดใช้งาน',
      cancelButtonText: 'ยกเลิก'
    });

    if (!result.isConfirmed) return;

    try {
      const currentEmployee = employees.find(emp => emp.employee_id === employeeId);
      const updatedEmployee = { ...currentEmployee, is_active: 1 };
      
      const data = await api.updateEmployee(updatedEmployee);

      if (data.success) {
        setEmployees(prev => prev.map(emp => emp.employee_id === employeeId ? { ...emp, is_active: 1 } : emp));
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เปิดการใช้งานบุคลากรเรียบร้อยแล้ว',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถเปิดการใช้งานได้: ' + data.error, 'error');
      }
    } catch (error) {
      console.error('Error reactivating employee:', error);
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error');
    }
  };

  const startCamera = async () => {
    const isSecureContext = window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';

    if (!isSecureContext) {
      Swal.fire('แจ้งเตือน', 'ระบบต้องการ HTTPS เพื่อใช้งานกล้องบนมือถือ กรุณาใช้งานผ่าน HTTPS', 'warning');
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      Swal.fire('แจ้งเตือน', 'เบราว์เซอร์ของคุณไม่รองรับการใช้งานกล้อง', 'warning');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints());
      
      if (!videoRef.current) {
        console.error('Video ref not found');
        return;
      }

      videoRef.current.srcObject = stream;
      setCameraActive(true);
      setRegistrationResult(null);
      
    } catch (error) {
      console.error('Camera error details:', error);
      
      let errorMessage = 'ไม่สามารถเข้าถึงกล้องได้';
      
      if (error.name === 'OverconstrainedError') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(getSimpleCameraConstraints());
          videoRef.current.srcObject = stream;
          setCameraActive(true);
          setRegistrationResult(null);
          return;
        } catch (retryError) {
          errorMessage = 'การตั้งค่ากล้องไม่รองรับ กรุณาลองในเบราว์เซอร์อื่น';
        }
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'กรุณาอนุญาตการใช้งานกล้องในเบราว์เซอร์';
      }
      
      Swal.fire('เกิดข้อผิดพลาด', errorMessage, 'error');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
      setRegistering(false);
    }
  };

  const registerFace = async () => {
    if (!selectedEmployee || registering) return;

    setRegistering(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const displaySize = { width: video.videoWidth, height: video.videoHeight };
      faceapi.matchDimensions(canvas, displaySize);
      
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.SsdMobilenetv1Options())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, [resizedDetection]);
        faceapi.draw.drawFaceLandmarks(canvas, [resizedDetection]);
        
        const data = await api.registerFace({
          employee_id: selectedEmployee.employee_id,
          face_encodings: Array.from(detection.descriptor)
        });
        
        if (data.success) {
          setRegistrationResult({
            success: true,
            message: 'ลงทะเบียนใบหน้าเรียบร้อยแล้ว'
          });
          stopCamera();
          
          setEmployees(prev => prev.map(emp => 
            emp.employee_id === selectedEmployee.employee_id ? { ...emp, has_registered_face: 1 } : emp
          ));

        } else {
          setRegistrationResult({
            success: false,
            message: data.error || 'ไม่สามารถลงทะเบียนได้'
          });
        }
      } else {
        Swal.fire('แจ้งเตือน', 'ไม่พบใบหน้าในกรอบ กรุณาลองใหม่', 'warning');
      }
      
    } catch (error) {
      console.error('Error registering face:', error);
      setRegistrationResult({
        success: false,
        message: 'เกิดข้อผิดพลาด: ' + error.message
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleViewDeviceUsers = async () => {
    setShowDeviceUsersModal(true);
    setLoadingDeviceUsers(true);
    try {
      const response = await api.getDeviceUsers();
      if (response.success) {
        setDeviceUsers(response.data || []);
      } else {
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูลจากเครื่องได้: ' + response.error, 'error');
      }
    } catch (error) {
      console.error('Error fetching device users:', error);
      Swal.fire('เกิดข้อผิดพลาด', 'เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์', 'error');
    } finally {
      setLoadingDeviceUsers(false);
    }
  };

  const handleDeletePasskey = async (employeeId, name) => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบอุปกรณ์',
      text: `คุณต้องการลบการลงทะเบียนอุปกรณ์ของ ${name} ใช่หรือไม่?\nการดำเนินการนี้จะทำให้ผู้ใช้ต้องลงทะเบียนอุปกรณ์ใหม่เมื่อเข้าระบบครั้งถัดไป`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบอุปกรณ์',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.fetch(`webauthn/delete-passkey`, {
          method: 'DELETE',
          body: JSON.stringify({ employee_id: employeeId })
        });

        if (response.success) {
          Swal.fire('ลบสำเร็จ!', 'ลบการลงทะเบียนอุปกรณ์สำเร็จ', 'success');
          
          setEmployees(prevEmployees => 
            prevEmployees.map(emp => 
              emp.employee_id === employeeId 
                ? { ...emp, has_passkey: 0 } 
                : emp
            )
          );
        } else {
          Swal.fire('เกิดข้อผิดพลาด', response.error || 'เกิดข้อผิดพลาดในการลบ', 'error');
        }
      } catch (error) {
        console.error('Delete passkey error:', error);
        Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถลบพาสคีย์ได้', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">จัดการบุคลากร</h1>
        
        {/* ✅ จัดกลุ่มปุ่มด้านขวาบน และปรับธีมให้เข้ากัน */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handleViewDeviceUsers}
            className="bg-white text-blue-600 border border-blue-200 px-4 py-2.5 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors shadow-sm font-medium"
          >
            <HardDrive className="h-5 w-5 mr-2" />
            จัดการผู้ใช้ (ZKTeco)
          </button>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors shadow-sm font-medium"
          >
            <UserPlus className="h-5 w-5" />
            <span>เพิ่มบุคลากร</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200 mt-6">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <h2 className="text-lg font-semibold text-gray-900">
              รายชื่อบุคลากร ({filteredEmployees.length} คน)
            </h2>
            
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              >
                <option value="all">ทุกสถานะ</option>
                <option value="active">ใช้งานอยู่</option>
                <option value="inactive">ปิดการใช้งาน</option>
              </select>

              <div className="relative shadow-sm">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="ค้นหาบุคลากร..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รหัส</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เบอร์โทรศัพท์</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ตำแหน่ง</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แผนก/กลุ่มสาระฯ</th>
                {/* ✅ เปลี่ยนให้จัดกึ่งกลางเพื่อความสวยงามของไอคอน */}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Passkey</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ใบหน้า</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เก็บภาพ</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">การจัดการ</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredEmployees.map((employee) => (
                <tr key={employee.employee_id} className={`hover:bg-gray-50 transition-colors ${employee.is_active == 0 ? 'opacity-60 bg-gray-50' : ''} ${employee.employee_id === 'ADMIN' || employee.position === 'ผู้ดูแลระบบ' ? 'bg-blue-50/30' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {employee.employee_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {employee.first_name} {employee.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {employee.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {employee.academic_position || employee.position || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {employee.department || '-'}
                  </td>
                  
                  {/* ✅ คอลัมน์ Passkey: แสดงเฉพาะไอคอน */}
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    {employee.has_passkey > 0 ? (
                      <div className="flex justify-center" title="ผูกอุปกรณ์แล้ว">
                        <div className="p-1.5 bg-green-100 text-green-700 rounded-full border border-green-200">
                          <ShieldCheck className="w-4 h-4" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center" title="ยังไม่ได้ผูกอุปกรณ์">
                        <div className="p-1.5 bg-gray-100 text-gray-400 rounded-full border border-gray-200">
                          <Fingerprint className="w-4 h-4" />
                        </div>
                      </div>
                    )}
                  </td>
                  
                  {/* ✅ คอลัมน์สถานะใบหน้า: แสดงเฉพาะไอคอน */}
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    {employee.has_registered_face > 0 ? (
                      <div className="flex justify-center" title="มีข้อมูลใบหน้า (Web Face)">
                        <div className="p-1.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200">
                          <CheckCircle className="w-4 h-4" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center" title="ยังไม่มีข้อมูลใบหน้า (Web Face)">
                        <div className="p-1.5 bg-red-100 text-red-500 rounded-full border border-red-200">
                          <XCircle className="w-4 h-4" />
                        </div>
                      </div>
                    )}
                  </td>
                  
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${
                        employee.save_attendance_image == 1
                          ? 'bg-blue-50 text-blue-700 border-blue-200' 
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                        {employee.save_attendance_image == 1 ? 'เก็บบันทึก' : 'ไม่เก็บ'}
                      </span>
                    </td>
                  )}
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${
                      employee.is_active == 1
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {employee.is_active == 1 ? 'ใช้งานอยู่' : 'ระงับการใช้งาน'}
                    </span>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1.5">
                        {employee.employee_id !== 'ADMIN' && employee.position !== 'ผู้ดูแลระบบ' ? (
                          <>
                            <button
                              onClick={() => {
                                setSelectedEmployee({
                                  ...employee,
                                  phone: employee.phone || '',
                                  address: employee.address || '',
                                  academic_position: employee.academic_position || ''
                                });
                                setShowEditModal(true);
                              }}
                              className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 rounded-full transition-colors"
                              title="แก้ไขข้อมูล"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            
                            {employee.is_active == 1 && (
                              <button
                                onClick={() => {
                                  setSelectedEmployee(employee);
                                  setShowFaceModal(true);
                                }}
                                className="p-1.5 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-800 rounded-full transition-colors"
                                title="ลงทะเบียนใบหน้า (สำหรับ Web)"
                              >
                                <Camera className="h-4 w-4" />
                              </button>
                            )}

                            {employee.has_passkey > 0 && (
                              <button
                                onClick={() => handleDeletePasskey(employee.employee_id, `${employee.first_name} ${employee.last_name}`)}
                                className="p-1.5 bg-orange-50 text-orange-500 hover:bg-orange-100 hover:text-orange-700 rounded-full transition-colors"
                                title="ลบอุปกรณ์ (Passkey) ที่ผูกไว้"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                            
                            {employee.is_active == 1 ? (
                              <button
                                onClick={() => handleDeactivateEmployee(employee.employee_id, employee.first_name, employee.last_name)}
                                className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 rounded-full transition-colors"
                                title="ปิดการใช้งาน"
                              >
                                <UserX className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivateEmployee(employee.employee_id, employee.first_name, employee.last_name)}
                                className="p-1.5 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-800 rounded-full transition-colors"
                                title="เปิดการใช้งาน"
                              >
                                <User className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic px-2">ผู้ดูแลระบบ (System)</span>
                        )}
                      </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 bg-white">
            <Users className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบบุคลากร</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'ไม่มีบุคลากรที่ตรงกับเงื่อนไขการค้นหา'
                : 'เริ่มต้นด้วยการเพิ่มบุคลากรคนแรก'}
            </p>
          </div>
        )}
      </div>

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center"><UserPlus className="mr-2 h-6 w-6 text-blue-500" /> เพิ่มบุคลากรใหม่</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รหัสบุคลากร *</label>
                <input
                  type="text"
                  value={newEmployee.employee_id}
                  onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น EMP001"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เลขบัตรประชาชน *</label>
                <input
                  type="text"
                  value={newEmployee.national_id}
                  onChange={(e) => setNewEmployee({...newEmployee, national_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="13 หลัก"
                  maxLength="13"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ *</label>
                  <input
                    type="text"
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล *</label>
                  <input
                    type="text"
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>เบอร์โทรศัพท์</span>
                  </div>
                </label>
                <input
                  type="tel"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น 0812345678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Home className="h-4 w-4 text-gray-500" />
                    <span>ที่อยู่ (ย่อ)</span>
                  </div>
                </label>
                <textarea
                  rows="2"
                  value={newEmployee.address}
                  onChange={(e) => setNewEmployee({...newEmployee, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น 123/45 ต.บางใหญ่ อ.บางใหญ่ จ.นนทบุรี"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ตำแหน่ง (Role) *</label>
                <select
                  value={newEmployee.position}
                  onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">เลือกตำแหน่ง</option>
                  {isAdmin && <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>}
                  <option value="เจ้าหน้าที่">เจ้าหน้าที่</option>
                  <option value="บุคลากร">บุคลากร</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span>ตำแหน่งทางวิชาการ</span>
                  </div>
                </label>
                <select
                  value={newEmployee.academic_position}
                  onChange={(e) => setNewEmployee({...newEmployee, academic_position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {academicPositions.map(pos => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">แผนก/กลุ่มสาระฯ</label>
                <select
                  value={newEmployee.department}
                  onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.department_name}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  (หากไม่มีแผนกที่ต้องการ กรุณาไปที่หน้า "จัดการแผนก" ก่อน)
                </p>
              </div>

              {isAdmin && (
                <div className="pt-2 border-t">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEmployee.save_attendance_image}
                      onChange={(e) => setNewEmployee({
                        ...newEmployee, 
                        save_attendance_image: e.target.checked
                      })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <Image className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          เก็บภาพเวลาสแกนหน้า
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        บันทึกภาพถ่ายเมื่อบุคลากรคนนี้สแกนหน้าลงเวลา
                      </p>
                    </div>
                  </label>
                </div>
              )}

            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleAddEmployee}
                disabled={!newEmployee.employee_id || !newEmployee.national_id || !newEmployee.first_name || !newEmployee.last_name || !newEmployee.position}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                เพิ่มบุคลากร
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 border border-gray-300 py-3 px-4 rounded-md hover:bg-gray-200 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center"><Edit className="mr-2 h-5 w-5 text-blue-500" /> แก้ไขข้อมูลบุคลากร</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รหัสบุคลากร</label>
                <input
                  type="text"
                  value={selectedEmployee.employee_id}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เลขบัตรประชาชน *</label>
                <input
                  type="text"
                  value={selectedEmployee.national_id}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, national_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength="13"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ *</label>
                  <input
                    type="text"
                    value={selectedEmployee.first_name}
                    onChange={(e) => setSelectedEmployee({...selectedEmployee, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล *</label>
                  <input
                    type="text"
                    value={selectedEmployee.last_name}
                    onChange={(e) => setSelectedEmployee({...selectedEmployee, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>เบอร์โทรศัพท์</span>
                  </div>
                </label>
                <input
                  type="tel"
                  value={selectedEmployee.phone || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Home className="h-4 w-4 text-gray-500" />
                    <span>ที่อยู่ (ย่อ)</span>
                  </div>
                </label>
                <textarea
                  rows="2"
                  value={selectedEmployee.address || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ตำแหน่ง (Role) *</label>
                <select
                  value={selectedEmployee.position || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">เลือกตำแหน่ง</option>
                  {isAdmin && <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>}
                  <option value="เจ้าหน้าที่">เจ้าหน้าที่</option>
                  <option value="บุคลากร">บุคลากร</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span>ตำแหน่งทางวิชาการ</span>
                  </div>
                </label>
                <select
                  value={selectedEmployee.academic_position || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, academic_position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {academicPositions.map(pos => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">แผนก/กลุ่มสาระฯ</label>
                <select
                  value={selectedEmployee.department || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.department_name}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
              </div>

              {isAdmin && (
                <div className="pt-2 border-t">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEmployee.save_attendance_image != 0}
                      onChange={(e) => setSelectedEmployee({
                        ...selectedEmployee, 
                        save_attendance_image: e.target.checked ? 1 : 0
                      })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <Image className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          เก็บภาพเวลาสแกนหน้า
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        บันทึกภาพถ่ายเมื่อบุคลากรคนนี้สแกนหน้าลงเวลา
                      </p>
                    </div>
                  </label>
                </div>
              )}

            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleEditEmployee}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 transition-colors shadow-sm"
              >
                บันทึกการแก้ไข
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedEmployee(null);
                }}
                className="flex-1 bg-gray-100 text-gray-700 border border-gray-300 py-3 px-4 rounded-md hover:bg-gray-200 transition-colors shadow-sm"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Face Registration Modal */}
      {showFaceModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
               <Camera className="mr-2 h-6 w-6 text-purple-500"/> ลงทะเบียนใบหน้า - {selectedEmployee.first_name} {selectedEmployee.last_name}
            </h2>
            
            <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-6 shadow-inner" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ 
                  display: cameraActive ? 'block' : 'none',
                  transform: 'scaleX(-1)'
                }}
              />
              
              <canvas 
                ref={canvasRef} 
                className="absolute inset-0 pointer-events-none"
                style={{ 
                  display: cameraActive ? 'block' : 'none',
                  transform: 'scaleX(-1)'
                }}
              />
              
              {!cameraActive && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="text-center">
                    <UserPlus className="h-20 w-20 text-gray-500 mx-auto mb-4" />
                    <p className="text-xl text-gray-300 font-medium">พร้อมลงทะเบียนใบหน้า</p>
                    <p className="text-sm text-gray-500 mt-2">กดปุ่มเพื่อเริ่มต้นเปิดกล้อง</p>
                  </div>
                </div>
              )}

              {registering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-xl font-medium tracking-wide">กำลังประมวลผลใบหน้า...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {!cameraActive && !registrationResult && (
                <button
                  onClick={startCamera}
                  className="w-full bg-blue-500 text-white py-4 px-6 rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2 text-lg transition-colors shadow-md"
                >
                  <Camera className="h-6 w-6" />
                  <span>เปิดกล้องเพื่อลงทะเบียน</span>
                </button>
              )}

              {cameraActive && !registering && (
                <div className="flex space-x-4">
                  <button
                    onClick={registerFace}
                    className="flex-1 bg-green-500 text-white py-4 px-6 rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2 text-lg transition-colors shadow-md"
                  >
                    <CheckCircle className="h-6 w-6" />
                    <span>กดถ่ายภาพและบันทึก</span>
                  </button>
                  <button
                    onClick={stopCamera}
                    className="bg-gray-500 text-white py-4 px-6 rounded-lg hover:bg-gray-600 transition-colors shadow-md"
                  >
                    ปิดกล้อง
                  </button>
                </div>
              )}

              {registrationResult && (
                <div className="animate-in fade-in zoom-in duration-300">
                  <div className={`p-5 rounded-lg border-2 ${registrationResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className={`flex items-center space-x-3 ${registrationResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {registrationResult.success ? <CheckCircle className="h-8 w-8" /> : <AlertCircle className="h-8 w-8" />}
                      <p className="text-lg font-bold">{registrationResult.message}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowFaceModal(false);
                      setSelectedEmployee(null);
                      setRegistrationResult(null);
                      setCameraActive(false);
                    }}
                    className="w-full mt-4 bg-gray-900 text-white py-4 px-6 rounded-lg hover:bg-gray-800 transition-colors shadow-md font-medium"
                  >
                    เสร็จสิ้นและกลับสู่หน้าหลัก
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setShowFaceModal(false);
                  setSelectedEmployee(null);
                  setRegistrationResult(null);
                  stopCamera();
                }}
                className="w-full bg-transparent text-gray-500 hover:bg-gray-100 py-3 px-4 rounded-md transition-colors"
              >
                ยกเลิกและปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ดูผู้ใช้งานในเครื่องสแกน */}
      {showDeviceUsersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <HardDrive className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ผู้ใช้งานในเครื่องสแกน ZKTeco</h2>
              </div>
              <button
                onClick={() => setShowDeviceUsersModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto bg-gray-50">
              {loadingDeviceUsers ? (
                <div className="flex flex-col justify-center items-center h-40 space-y-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600"></div>
                  <span className="text-gray-500 font-medium tracking-wide">กำลังเชื่อมต่อและดึงข้อมูลจากเครื่องสแกน...</span>
                </div>
              ) : deviceUsers.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200 border-dashed">
                   <HardDrive className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">ไม่พบข้อมูลผู้ใช้งานในเครื่อง หรือดึงข้อมูลไม่สำเร็จ</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          รหัส (PIN)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ชื่อ (Name)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ระดับสิทธิ์
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          สถานะข้อมูลใบหน้า
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {deviceUsers.map((user, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {user.pin}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                            {user.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.privilege === '14' ? (
                              <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                                ผู้ดูแลระบบเครื่อง
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                                ผู้ใช้ทั่วไป
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.has_face ? (
                               <span className="text-green-600 font-medium flex items-center"><CheckCircle className="h-4 w-4 mr-1.5" /> มีข้อมูลใบหน้า</span>
                            ) : (
                               <span className="text-gray-400 flex items-center"><XCircle className="h-4 w-4 mr-1.5" /> ไม่มีข้อมูล</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-white flex justify-end rounded-b-xl">
              <button
                onClick={() => setShowDeviceUsersModal(false)}
                className="bg-gray-100 text-gray-700 border border-gray-300 px-6 py-2.5 rounded-lg hover:bg-gray-200 transition-colors font-medium shadow-sm"
              >
                ปิดหน้าต่าง
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;